import { useQueryClient } from "@tanstack/react-query";
import React, { FC, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  getListDocumentsQueryKey,
  useListDocuments,
} from "~/api/default/default";
import { useCopyDocumentsVolumes, useGetVolumes } from "~/api/folders/folders";
import { FolderChatStatus } from "~/api/model/folderChatStatus.ts";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { ROUTES, useProjectParams } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import { useParentCase } from "../hooks/useParentCase.ts";
import { useDocumentSelectionStore } from "../stores/documentSelectionStore";

interface AddToVersionDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const AddToVersionModal: FC<AddToVersionDialogProps> = ({
  open,
  setOpen,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const { project: parentCase } = useParentCase();
  const { selectedDocumentIds } = useDocumentSelectionStore();
  const { data: volumes } = useGetVolumes(organizationId, caseId);
  const { mutateAsync: copyDocuments, isPending } = useCopyDocumentsVolumes();

  const navigate = useNavigate();

  const intl = useIntl();
  const queryClient = useQueryClient();

  const versionOptions = useMemo(
    () =>
      volumes
        ?.filter((version) => version.chatStatus === FolderChatStatus.COMPLETE)
        .reduce(
          (acc, version) => {
            if (version.parentId !== null) {
              acc.push({ id: version.id, label: version.name });
            }
            return acc;
          },
          [] as { id: string; label: string }[]
        )
        .sort((a, b) => {
          const versionA = volumes?.find((v) => v.id === a.id);
          const versionB = volumes?.find((v) => v.id === b.id);

          return (
            new Date(versionB?.createdAt || 0).getTime() -
            new Date(versionA?.createdAt || 0).getTime()
          );
        }) || [],
    [volumes]
  );

  const [selectedVersionId, setSelectedVersionId] = useState<string>(
    versionOptions[0]?.id || ""
  );
  const { data: selectedVersionDocuments, isLoading } = useListDocuments(
    organizationId,
    selectedVersionId
  );
  const [duplicateDocuments, setDuplicateDocuments] = useState<
    { id: string; filename: string }[]
  >([]);

  useEffect(() => {
    if (!open) {
      setSelectedVersionId(versionOptions[0]?.id || "");
      setDuplicateDocuments([]);
    }
  }, [open, versionOptions]);

  const submit = async () => {
    trackEvent({
      eventName: "app/add_to_version_modal/send_files.button/submit",
    });

    if (!selectedVersionId) {
      return;
    }

    // Filter only unique documents
    const uniqueDocumentIds = selectedDocumentIds.filter(
      (id) => !selectedVersionDocuments?.documents?.some((doc) => doc.id === id)
    );

    if (uniqueDocumentIds.length === 0) {
      return;
    }

    if (!parentCase?.id) {
      return;
    }

    await copyDocuments({
      organizationId,
      projectId: parentCase.id,
      volumeId: selectedVersionId,
      data: {
        documentIds: uniqueDocumentIds,
      },
    });

    await queryClient.invalidateQueries({
      queryKey: getListDocumentsQueryKey(organizationId, selectedVersionId),
    });

    navigate(
      ROUTES.CASE({
        organizationId,
        caseId: selectedVersionId,
      })
    );
  };

  const handleSendFiles = async () => {
    // Refetch documents list
    await queryClient.invalidateQueries({
      queryKey: getListDocumentsQueryKey(organizationId, selectedVersionId),
    });

    const duplicates =
      selectedVersionDocuments?.documents?.filter((doc) =>
        selectedDocumentIds.includes(doc.id)
      ) || [];

    if (duplicates.length > 0) {
      setDuplicateDocuments(duplicates);
      return;
    }

    await submit();
  };
  const handleVersionChange = (versionId: string) => {
    setSelectedVersionId(versionId);
    setDuplicateDocuments([]);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[608px]">
        <DialogHeader>
          <DialogTitle>
            {duplicateDocuments.length > 0 ? (
              <FormattedMessage
                id="addToVersion.duplicateFiles.titleDetected"
                defaultMessage="Duplicate Detected"
              />
            ) : (
              <FormattedMessage
                id="addToVersion"
                defaultMessage="Add to Version"
              />
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground text-sm font-normal">
            {duplicateDocuments.length > 0 ? (
              <FormattedMessage
                id="addToVersion.duplicateFiles.existingFiles"
                defaultMessage="This already exists:"
              />
            ) : (
              <FormattedMessage
                id="addToVersion.hint"
                defaultMessage="Select the version to which you want to send the selected files."
              />
            )}
          </p>
        </div>
        {duplicateDocuments.length > 0 ? (
          <ul className="list-disc pl-4 text-sm">
            {duplicateDocuments.map((doc) => (
              <li key={doc.id}>{doc.filename}</li>
            ))}
          </ul>
        ) : (
          <div className="flex flex-col gap-2">
            <Label>
              <FormattedMessage
                id="addToVersion.selectVersion"
                defaultMessage="Select version"
              />
            </Label>
            <Select
              onValueChange={handleVersionChange}
              value={selectedVersionId}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={intl.formatMessage({
                    id: "selectVersionPlaceholder",
                    defaultMessage: "Select a version",
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {versionOptions.map((option) => (
                    <React.Fragment key={option.id}>
                      <SelectItem value={option.id}>{option.label}</SelectItem>
                    </React.Fragment>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        )}
        {duplicateDocuments.length > 0 &&
          duplicateDocuments.length < selectedDocumentIds.length && (
            <p className="text-muted-foreground text-sm font-normal ">
              <FormattedMessage
                id="addToVersion.duplicateFiles.skipMessage"
                defaultMessage="Existing files will be skipped."
              />
            </p>
          )}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            disabled={isLoading || isPending}
            onClick={() => {
              trackEvent({
                eventName: "app/add_to_version_modal/cancel.button/click",
              });
              setOpen(false);
            }}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          {duplicateDocuments.length > 0 ? (
            duplicateDocuments.length < selectedDocumentIds.length && (
              <Button
                disabled={
                  isLoading ||
                  isPending ||
                  !selectedVersionId ||
                  !parentCase?.id
                }
                onClick={submit}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <FormattedMessage id="continue" defaultMessage="Continue" />
              </Button>
            )
          ) : (
            <Button
              disabled={
                isLoading || isPending || !selectedVersionId || !parentCase?.id
              }
              onClick={handleSendFiles}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <FormattedMessage
                id="addToVersion.sendFiles"
                defaultMessage="Send Files"
              />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
